/**
 * Live Tracking Service
 * ─────────────────────
 * • Derives the WebSocket host automatically from REACT_APP_API_BASE_URL:
 *     Dev  → wss://devapi.hrms.lagran.in/ws/tracking/live/
 *     Live → wss://hrms.lagran.in/ws/tracking/live/
 *
 * • Sends a location_update payload every 5 seconds via WebSocket (primary).
 * • Falls back to a REST POST (attendance/api/employee/live-location/) if the
 *   WebSocket is not OPEN, so the backend always receives updates.
 *
 * Payload shape (both WS and REST):
 * {
 *   "type"      : "location_update",
 *   "latitude"  : 17.385044,
 *   "longitude" : 78.486671,
 *   "accuracy"  : 8
 * }
 */

import { postLiveLocation } from "../ApiHelper/url_Helpers";

class LiveTrackingService {
  constructor() {
    this.ws = null;
    this.intervalId = null;
    this.reconnectTimeout = null;
    this.currentPosition = null;
    this.watchId = null;
    this.isRunning = false;
  }

  // ─────────────────────────────────────────────────────────────
  // Build WebSocket URL from the same env var the REST client uses
  // ─────────────────────────────────────────────────────────────
  getWsUrl() {
    const apiBase =
      process.env.REACT_APP_API_BASE_URL || window.location.origin;

    // Determine ws / wss based on http / https in the base URL
    let wsProto;
    if (apiBase.startsWith("http://")) {
      wsProto = "ws:";
    } else if (apiBase.startsWith("https://")) {
      wsProto = "wss:";
    } else {
      wsProto = window.location.protocol === "https:" ? "wss:" : "ws:";
    }

    // Strip protocol prefix and trailing slashes → keep only host (+ port)
    const host = apiBase.replace(/^https?:\/\//i, "").replace(/\/+$/, "");

    // Auth token passed as query param (Django Channels TokenAuthMiddleware)
    const token =
      sessionStorage.getItem("userToken") || localStorage.getItem("userToken");
    const query = token ? `?token=${encodeURIComponent(token)}` : "";

    const wsUrl = `${wsProto}//${host}/ws/tracking/live/${query}`;
    console.log(
      "[LiveTracking] WebSocket URL:",
      wsUrl.replace(/\?token=.+/, "?token=***")
    );
    return wsUrl;
  }

  // ─────────────────────────────────────────────────────────────
  // Start – idempotent; safe to call multiple times
  // ─────────────────────────────────────────────────────────────
  start() {
    if (this.isRunning) return;
    this.isRunning = true;
    console.log("[LiveTracking] Service started");

    this.startLocationWatch();
    this.connectWebSocket();

    // Tick every 5 seconds – sends via WS if open, otherwise via REST
    if (this.intervalId) clearInterval(this.intervalId);
    this.intervalId = setInterval(() => {
      this.sendLocationUpdate();
    }, 5000);
  }

  // ─────────────────────────────────────────────────────────────
  // Stop – clean up everything
  // ─────────────────────────────────────────────────────────────
  stop() {
    this.isRunning = false;
    console.log("[LiveTracking] Service stopped");

    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
    if (this.watchId !== null && navigator.geolocation) {
      navigator.geolocation.clearWatch(this.watchId);
      this.watchId = null;
    }
    if (this.ws) {
      try {
        this.ws.close();
      } catch (e) {
        console.warn("[LiveTracking] Error closing WebSocket:", e);
      }
      this.ws = null;
    }
  }

  // ─────────────────────────────────────────────────────────────
  // GPS – get initial fix + watch for continuous updates
  // ─────────────────────────────────────────────────────────────
  startLocationWatch() {
    if (!navigator.geolocation) {
      console.warn("[LiveTracking] Geolocation is not supported.");
      return;
    }

    const successHandler = (pos) => {
      this.currentPosition = {
        latitude: Number(pos.coords.latitude.toFixed(6)),
        longitude: Number(pos.coords.longitude.toFixed(6)),
        accuracy: Math.round(pos.coords.accuracy || 10),
      };
    };

    const errorHandler = (err) =>
      console.warn("[LiveTracking] Geolocation error:", err.message);

    const geoOpts = {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 5000,
    };

    // Get an immediate fix
    navigator.geolocation.getCurrentPosition(
      successHandler,
      errorHandler,
      geoOpts
    );

    // Watch for position updates
    this.watchId = navigator.geolocation.watchPosition(
      successHandler,
      errorHandler,
      geoOpts
    );
  }

  // ─────────────────────────────────────────────────────────────
  // WebSocket – connect with auto-reconnect
  // ─────────────────────────────────────────────────────────────
  connectWebSocket() {
    if (!this.isRunning) return;

    try {
      const url = this.getWsUrl();
      this.ws = new WebSocket(url);

      this.ws.onopen = () => {
        console.log("[LiveTracking] WebSocket connected ✓");
        // Send immediately on open if we already have a position
        this.sendLocationUpdate();
      };

      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          window.dispatchEvent(
            new CustomEvent("wsTrackingMessage", { detail: data })
          );
        } catch (_) {
          // Ignore non-JSON frames
        }
      };

      this.ws.onerror = () => {
        console.warn("[LiveTracking] WebSocket error – will rely on REST fallback");
      };

      this.ws.onclose = (event) => {
        console.log(
          `[LiveTracking] WebSocket closed (code ${event.code}) – reconnecting in 5 s`
        );
        this.ws = null;
        if (this.isRunning) {
          this.reconnectTimeout = setTimeout(() => {
            this.connectWebSocket();
          }, 5000);
        }
      };
    } catch (e) {
      console.error("[LiveTracking] Failed to open WebSocket:", e);
      if (this.isRunning) {
        this.reconnectTimeout = setTimeout(() => {
          this.connectWebSocket();
        }, 5000);
      }
    }
  }

  // ─────────────────────────────────────────────────────────────
  // 5-second tick handler
  // Primary  → send over WebSocket (if OPEN)
  // Fallback → POST to REST endpoint
  // ─────────────────────────────────────────────────────────────
  sendLocationUpdate() {
    if (!this.currentPosition) {
      // Position not resolved yet – try to get it on the fly
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            this.currentPosition = {
              latitude: Number(pos.coords.latitude.toFixed(6)),
              longitude: Number(pos.coords.longitude.toFixed(6)),
              accuracy: Math.round(pos.coords.accuracy || 10),
            };
            this._dispatch(this.currentPosition);
          },
          (err) =>
            console.warn(
              "[LiveTracking] Could not get position for update:",
              err.message
            ),
          { enableHighAccuracy: true, timeout: 5000 }
        );
      }
      return;
    }

    this._dispatch(this.currentPosition);
  }

  // ─────────────────────────────────────────────────────────────
  // Internal: send via WebSocket; fallback to REST if WS not OPEN
  // ─────────────────────────────────────────────────────────────
  _dispatch(pos) {
    const payload = {
      type: "location_update",
      latitude: pos.latitude,
      longitude: pos.longitude,
      accuracy: pos.accuracy,
    };

    const wsOpen = this.ws && this.ws.readyState === WebSocket.OPEN;

    if (wsOpen) {
      try {
        this.ws.send(JSON.stringify(payload));
        console.log(
          `[LiveTracking] WS → lat:${pos.latitude} lon:${pos.longitude} acc:${pos.accuracy}m`
        );
      } catch (e) {
        console.error("[LiveTracking] WS send failed:", e);
        this._restFallback(payload);
      }
    } else {
      // WebSocket not ready – use REST API
      this._restFallback(payload);
    }
  }

  // ─────────────────────────────────────────────────────────────
  // REST fallback: POST to attendance/api/employee/live-location/
  // ─────────────────────────────────────────────────────────────
  _restFallback(payload) {
    postLiveLocation(payload)
      .then(() => {
        console.log(
          `[LiveTracking] REST → lat:${payload.latitude} lon:${payload.longitude} acc:${payload.accuracy}m`
        );
      })
      .catch((err) => {
        console.warn("[LiveTracking] REST fallback failed:", err?.message || err);
      });
  }
}

export const liveTrackingService = new LiveTrackingService();
export default liveTrackingService;
